import { claimDetailFooterSlot, provideDetailFooterSlot } from '@modules/common/components/detail-breadcrumbs/detail-footer-slot';
import { APP_CRUMBS, TREE_CRUMBS, UNTITLED_CRUMB, DetailNavigation } from '@modules/common/components/detail-breadcrumbs/detail-navigation';
import { type DetailCrumb } from '@modules/common/components/detail-breadcrumbs/detail-breadcrumbs.component';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { CalendarTenantScopeService } from '@modules/calendar-app/services/calendar-tenant-scope.service';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { WorkspaceIdentityService } from '@modules/common/services/workspace-identity.service';
import { type StitchRequestConfig } from '@modules/common/services/base-stitch.service';
import { Component, signal, inject, Injector, Input, OnInit, type SimpleChanges } from '@angular/core';
import { AlertService } from '@modules/alert/services/alert.service';
import { CalendarsService } from '@modules/calendar-app/services/calendars.service';
import { EventsListStateService } from '@modules/calendar-app/services/events-list-state.service';
import { EventsService } from '@modules/calendar-app/services/events.service';
import { RecurrenceEditModalService } from '@modules/calendar-app/services/recurrence-edit-modal.service';
import { CalendarAppStateService } from '@modules/calendar-app/services/state.service';
import { Calendar, type CalendarFormGroupConfig } from '@modules/calendar-app/types/calendar';
import { EventsFilters } from '@modules/calendar-app/types/events-filters';
import { EventsListState } from '@modules/calendar-app/types/events-list-state';
import { FullFormBaseComponent } from '@modules/common/components/full-form-base/full-form-base.component';
import { TimeZoneSeedingController } from '@modules/form-controls/services/timezone-seeding.controller';
import { AccountTimeZoneSettingsService } from '@modules/account/services/account-timezone-settings.service';
import { type ContainerDndConfig, ContainerDndService } from '@modules/common/services/container-dnd.service';
import { Tab } from '@modules/common/types/tab';
import { type DragData, DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import type { AutocompleteFactory } from '@frontendlib/common/types/autocomplete-factory';
import { StateKey } from '@modules/settings/types/state-key';
import { StitchType } from '@sharedlib/common/types/StitchType';
import type { CalendarEventSummaryDto } from '@sharedlib/dtos/dto/calendar-app/event.dto';
import { switchMap, takeUntil } from 'rxjs/operators';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';

import { catchError, combineLatest, filter, of, skip, take, tap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

declare module '@modules/settings/types/state' {
  export interface State {
    [StateKey.eventsFFListState]?: EventsListState;
  }
}

@Component({
  selector: 'app-calendar-form',
  templateUrl: './calendar-form.component.html',
  styleUrls: ['./calendar-form.component.less'],
  standalone: false,
  providers: [
    WorkspaceIdentityService,
    ...EventsListStateService.providers({
      stateKey: StateKey.eventsFFListState,
    }),
    provideDetailFooterSlot(),
  ],
})
export class CalendarFormComponent
  extends FullFormBaseComponent<StitchType.CALENDAR, Calendar, CalendarFormGroupConfig>
  implements OnInit
{
  protected readonly sharingTenantId = signal<string | null>(null);
  private readonly calendarTenantScope = inject(CalendarTenantScopeService, { optional: true });
  private readonly itemIdentityService = inject(WorkspaceIdentityService);
  protected readonly workspaceIdentity = toSignal(
    this.stitchItemChanged.pipe(
      skip(1),
      filter(() => !this.stitchItem?.id),
      take(1),
      switchMap(() =>
        this.spaceContextStore.type() === SpaceType.SHARED_SPACE
          ? this.itemIdentityService.current().pipe(take(1))
          : of(undefined),
      ),
      tap((identity) => {
        if (identity && !this.stitchItem?.id) {
          this.sharingTenantId.set(
            this.stitchItem?.accountId ?? this.calendarTenantScope?.effectiveTenantId() ?? identity.memberTenantId,
          );
        }
      }),
      catchError(() => of(undefined)),
    ),
    { initialValue: undefined },
  );

  public changesKey = 'calendar';
  public tabs: Tab[] = [
    { title: 'Events', value: 'calendar' },
    { title: 'Details', value: 'details' },
    { title: 'Attachments', value: 'attachments' },
    { title: 'Stitch', value: 'stitch' },
    { title: 'Comments', value: 'comments' },
  ];
  protected override attachmentsMenuStateVariant = 'calendars';
  public parentSuggestions: AutocompleteFactory<Calendar>;
  public eventsFilters: EventsFilters;

  protected dragDataType = DragDataTypes.calendar;
  protected override childItemDragType = DragDataTypes.event;
  protected override childItemContainerIdField = 'calendarId';

  @Input() calendar = new Calendar();

  /** stitch-app-shell breadcrumbs, as on every detail page. */
  protected readonly navigation = new DetailNavigation(CalendarAppStateService, { appLabel: APP_CRUMBS.calendar });
  /** The item list's pager joins the breadcrumbs row in the main view. */
  private readonly footerSlot = claimDetailFooterSlot(this.navigation.enabled);

  /** [workspace ›] Calendar app › Calendars › this calendar. */
  protected breadcrumbs(): DetailCrumb[] {
    return this.navigation.crumbs({
      view: TREE_CRUMBS.calendars,
      fallbackView: TREE_CRUMBS.calendars,
      itemTitle: this.stitchItem?.title || UNTITLED_CRUMB,
    });
  }

  private readonly eventsListStateService = inject(EventsListStateService, { self: true });
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;
  // #916: seeds a new item's zone from the person's own zone — the one the grid is drawn in.
  private readonly accountTimeZoneSettingsService = inject(AccountTimeZoneSettingsService);
  protected readonly tzSeeder = new TimeZoneSeedingController();
  private readonly containerDndService = inject(ContainerDndService);
  private readonly alertService = inject(AlertService);
  private readonly recurrenceEditModalService = inject(RecurrenceEditModalService);

  private dndConfig(): ContainerDndConfig {
    return {
      containerType: DragDataTypes.calendar,
      childItemType: DragDataTypes.event,
      childService: this.eventsService,
      containerIdField: 'calendarId',
      appStateService: this.stateService,
      spaceId: this.stitchItem.accountId ?? this.spaceContextStore.contextId(),
      copy: (ids, container, dragData) =>
        this.recurrenceEditModalService
          .askForItems(dragData.data as CalendarEventSummaryDto[], 'Copy recurring event?')
          .pipe(
            switchMap((mode) =>
              this.eventsService.duplicate(
                ids,
                container.id,
                { tenantItems: dragData.data as CalendarEventSummaryDto[] },
                mode,
              ),
            ),
          ),
      move: (ids, container, dragData) =>
        this.recurrenceEditModalService
          .askForItems(dragData.data as CalendarEventSummaryDto[], 'Move recurring event?')
          .pipe(
            switchMap((mode) =>
              this.eventsService.bunchUpdate(
                { ids },
                { calendarId: container.id },
                {
                  message: 'Successfully moved to ' + container.title,
                  tenantItems: dragData.data as CalendarEventSummaryDto[],
                },
                mode,
              ),
            ),
          ),
    };
  }

  constructor(
    private calendarsService: CalendarsService,
    private eventsService: EventsService,
    private stateService: CalendarAppStateService,
    injector: Injector,
  ) {
    super(injector, calendarsService);
    this.sharingTenantId.set(this.calendarTenantScope?.effectiveTenantId() ?? null);
  }

  ngOnInit() {
    super.ngOnInit();
    this.initTimeZoneSeeding();

    this.stitchItemChanged.pipe(takeUntil(this.alive)).subscribe(() => {
      this.parentSuggestions = this.calendarsService.getAutocompleteSuggestions(
        { exceptIds: [this.stitchItem.id] },
        () => owningTenantContext(this.tenantOwnerId()) ?? this.requestContext,
      );
    });

    this.stitchItemChanged
      .pipe(
        switchMap(() => combineLatest([this.eventsListStateService.value(), this.displayTimeZone$])),
        takeUntil(this.alive),
      )
      .subscribe(([, displayTimeZone]) => {
        this.eventsFilters = new EventsFilters({ containersIds: [this.stitchItem.id], displayTimeZone });
        this.eventsFilters.applyListState(this.eventsListStateService.state);
      });
  }

  /**
   * Actions
   */

  dndDrop(dragData: DragData): void {
    this.containerDndService.handleDndDrop(
      this.stitchItem,
      dragData,
      this.dndConfig(),
      this.stitchService,
      this.alertService,
    );
  }

  /**
   * A new calendar seeds its timezone from the account/Settings timezone, falling back to the
   * browser then UTC. Never overrides a hand-picked value and never touches existing calendars;
   * the picker shows where the value came from.
   */
  private initTimeZoneSeeding(): void {
    this.tzSeeder.start({
      control: this.form.controls.timeZone,
      destroyRef: this.destroyRef,
      isExistingEntity: !!this.calendar.id,
      settingsTimeZone$: this.accountTimeZoneSettingsService.timeZoneOnce(),
      rungs: ['settings', 'browser', 'utc'],
    });
  }

  get stitchItem(): Calendar {
    return this.calendar;
  }

  public override ngOnChanges(changes: SimpleChanges): void {
    if (changes.calendar && !this.calendar?.id) {
      const identity = this.workspaceIdentity();
      this.sharingTenantId.set(
        this.calendar?.accountId ?? this.calendarTenantScope?.effectiveTenantId() ?? identity?.memberTenantId ?? null,
      );
    }
    super.ngOnChanges(changes);
  }

  protected override tenantOwnerId(): string | undefined {
    return this.stitchItem?.accountId ?? (this.workspaceIdentity() ? (this.sharingTenantId() ?? undefined) : undefined);
  }

  protected override requestConfig(): StitchRequestConfig {
    if (this.stitchItem?.accountId) return { tenantItems: [this.stitchItem] };
    const tenantId = this.workspaceIdentity() ? this.sharingTenantId() : null;
    return tenantId ? { requestContext: owningTenantContext(tenantId) } : super.requestConfig();
  }

  protected shouldRefreshList(prev, current) {
    return Calendar.shouldRefreshList(prev, current);
  }

  protected fromFormGroup(): Calendar {
    return Calendar.fromFormGroup(this.form);
  }

  protected asFormGroup() {
    return this.calendar.asFormGroup({
      calendarsService: this.calendarsService,
      maxAttachmentSize: this.maxUploadSize(),
    });
  }
}
